import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../ui/tooltip';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Globe,
  FileText,
  Trash2,
  Upload,
  Download,
  RefreshCw,
} from 'lucide-react';
import { BasicsTab } from './tabs/BasicsTab';
import { MethodsTab } from './tabs/MethodsTab';
import { ConnectionsTab } from './tabs/ConnectionsTab';
import {
  useGetCuratedIntegration,
  usePublishCuratedIntegration,
  useUnpublishCuratedIntegration,
  useDeleteCuratedIntegration,
} from '../../../api/curated-integrations.api';
import type { UnpublishCuratedIntegrationResponse } from '../../../api/curated-integrations.types';
import { toast } from 'sonner';

type TabId = 'basics' | 'methods' | 'connections';

/**
 * Detail- und Edit-Page einer einzelnen Curated Integration.
 * Route: /integrations/:id
 * Tabs: Basics | Methods | Connections
 * Header-Actions: Publish / Unpublish / Delete
 */
export function IntegrationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabId>('basics');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unpublishResponse, setUnpublishResponse] = useState<UnpublishCuratedIntegrationResponse | null>(null);

  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetCuratedIntegration(id ?? '');

  const { mutateAsync: publish, isLoading: publishing } = usePublishCuratedIntegration();
  const { mutateAsync: unpublish, isLoading: unpublishing } = useUnpublishCuratedIntegration();
  const { mutateAsync: deleteIntegration, isLoading: deleting } = useDeleteCuratedIntegration();

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!id) return;
    try {
      await publish(id);
      toast.success('Integration veröffentlicht.');
      setPublishDialogOpen(false);
      void refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Veröffentlichen.';
      toast.error(message);
    }
  };

  const handleUnpublishConfirm = async () => {
    if (!id) return;
    try {
      const result = await unpublish(id);
      setUnpublishResponse(result);
      setUnpublishDialogOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Abrufen der Verbindungszahl.';
      toast.error(message);
    }
  };

  const handleUnpublishExecute = async () => {
    // The unpublish was already executed in handleUnpublishConfirm;
    // we now just close the dialog and refresh.
    toast.success('Integration zurückgezogen.');
    setUnpublishDialogOpen(false);
    setUnpublishResponse(null);
    void refetch();
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteIntegration(id);
      toast.success('Integration gelöscht.');
      navigate('/integrations');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Löschen.';
      toast.error(message);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" aria-label="Integration wird geladen…" />
      </div>
    );
  }

  if (error || !integration) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
      >
        <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <span className="text-sm">{error?.message ?? 'Integration nicht gefunden.'}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void refetch()}
          aria-label="Erneut versuchen"
        >
          Wiederholen
        </Button>
      </div>
    );
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const hasConnections = integration.connectionCount > 0;
  const canDelete = !hasConnections;

  const affectedCount = unpublishResponse?.affectedConnectionCount ?? 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/integrations')}
            aria-label="Zurück zur Integrationsliste"
            className="mt-0.5 -ml-1"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">
                {integration.displayName ?? integration.name}
              </h1>
              {integration.isPublished ? (
                <Badge
                  className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1"
                  aria-label="Status: Veröffentlicht"
                >
                  <Globe className="w-3 h-3" aria-hidden="true" />
                  Veröffentlicht
                </Badge>
              ) : (
                <Badge
                  className="bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-1"
                  aria-label="Status: Entwurf"
                >
                  <FileText className="w-3 h-3" aria-hidden="true" />
                  Entwurf
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5 font-mono">{integration.name}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Refresh */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            aria-label="Daten aktualisieren"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </Button>

          {/* Publish — only if Draft */}
          {!integration.isPublished && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPublishDialogOpen(true)}
              disabled={publishing}
              aria-label="Integration veröffentlichen"
              className="text-green-700 border-green-300 hover:bg-green-50"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
              ) : (
                <Upload className="w-4 h-4 mr-1.5" aria-hidden="true" />
              )}
              Veröffentlichen
            </Button>
          )}

          {/* Unpublish — only if Published */}
          {integration.isPublished && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUnpublishConfirm}
              disabled={unpublishing}
              aria-label="Integration zurückziehen"
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
            >
              {unpublishing ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" aria-hidden="true" />
              )}
              Zurückziehen
            </Button>
          )}

          {/* Delete */}
          {canDelete ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting}
              aria-label="Integration löschen"
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              )}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} aria-label="Löschen nicht möglich — aktive Verbindungen vorhanden">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled
                    aria-disabled="true"
                    className="text-red-300 border-red-200 cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Löschen nicht möglich, solange aktive Verbindungen existieren (
                {integration.connectionCount} Verbindung
                {integration.connectionCount !== 1 ? 'en' : ''}).
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabId)}
      >
        <TabsList aria-label="Integrations-Bereiche">
          <TabsTrigger value="basics" aria-label="Tab: Grundeinstellungen">
            Grundeinstellungen
          </TabsTrigger>
          <TabsTrigger value="methods" aria-label="Tab: Methoden">
            Methoden
            {integration.unacknowledgedChangesCount > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full"
                aria-label={`${integration.unacknowledgedChangesCount} offene Änderungen`}
              >
                {integration.unacknowledgedChangesCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="connections" aria-label="Tab: Verbindungen">
            Verbindungen
            {integration.connectionCount > 0 && (
              <span className="ml-1.5 text-xs text-gray-500">
                ({integration.connectionCount})
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="mt-4">
          <BasicsTab integration={integration} onSaved={() => void refetch()} />
        </TabsContent>

        <TabsContent value="methods" className="mt-4">
          <MethodsTab integration={integration} onSaved={() => void refetch()} />
        </TabsContent>

        <TabsContent value="connections" className="mt-4">
          <ConnectionsTab integrationId={integration.id} />
        </TabsContent>
      </Tabs>

      {/* Publish confirmation dialog */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Integration veröffentlichen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Integration wird für Kunden sichtbar und kann mit Agenten verbunden werden.
              Stellen Sie sicher, dass mindestens eine Methode in der Whitelist aktiviert ist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-600"
            >
              Veröffentlichen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unpublish confirmation dialog — F2: shows affectedConnectionCount */}
      <AlertDialog open={unpublishDialogOpen} onOpenChange={(open) => { if (!open) { setUnpublishDialogOpen(false); setUnpublishResponse(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Integration zurückziehen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Die Integration wird aus dem Kundenportal entfernt. Neue Verbindungen sind dann
                  nicht mehr möglich.
                </p>
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>
                    <strong>{affectedCount} aktive Kundenverbindung{affectedCount !== 1 ? 'en' : ''}</strong>{' '}
                    laufen weiter, aber Neu-Connects werden blockiert.
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublishExecute}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
            >
              Zurückziehen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Integration löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Integration und alle
              zugehörigen Konfigurationsdaten werden dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
